import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';
import { AddArtifactsDialog } from '@/features/artifacts/AddArtifactsDialog.js';
import { artifactUploadRejection } from '@/features/artifacts/artifact-editing.js';
import { artifactAttachmentRef } from '@/features/artifacts/artifact-attachment.js';
import { canAddArtifact } from '@/features/artifacts/artifact-display.js';
import {
  oneDriveAutomationBlock,
  type OneDriveAutomationBlock,
} from '@/features/onedrive/onedrive-permissions.js';
import { sharePointAttachmentRef } from '@/features/sharepoint/sharepoint-attachment.js';
import { useCurrentWorkspaceRole } from '@/features/workspace/useCurrentWorkspaceRole.js';
import type {
  ArtifactAttachmentRef,
  OneDriveAttachmentRef,
  SharePointAttachmentRef,
} from '@/integration/types.js';
import {
  oneDriveAccountQueryOptions,
  oneDriveScopesQueryOptions,
} from '@/lib/api/onedrive.js';
import type { SharePointSearchHit } from '@/lib/api/sharepoint.js';
import {
  useUploadArtifact,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';
import { putFileInWorkspace } from '@/lib/api/workspace-file-import.js';
import { notify } from '@/lib/notify.js';
import { AttachmentChip } from '@/ui/chat/atoms/AttachmentChip.js';
import { SkillChips, SlashCommandMenu } from '@/ui/chat/composer/index.js';
import { MentionMenu } from '@/ui/chat/composer/MentionMenu.js';
import { useMentionPopup } from '@/ui/chat/composer/useMentionPopup.js';

import {
  MAX_AUTOMATION_ATTACHMENTS,
  automationAttachment,
  automationAttachmentKey,
  currentAttachmentName,
  isOneDriveAutomationAttachment,
  oneDriveAutomationAttachment,
  withAutomationAttachments,
  type AutomationAttachment,
} from './automation-attachments.js';
import { FieldError, fieldErrorId } from './field-error.js';
import { type UseAutomationSkillsFieldResult } from './useAutomationSkillsField.js';

/** What to say about a file an automation would never be able to open, and what to do about it. */
function oneDriveRefusal(
  blocked: OneDriveAutomationBlock,
  name: string,
): string {
  if (blocked === 'connection') {
    return `Your OneDrive connection needs to be reconnected, so an automation cannot read “${name}”. Reconnect it, then attach the file again.`;
  }
  if (blocked === 'folder') {
    return `Automation access is off for the OneDrive folder holding “${name}”. Set it on that folder, then attach the file again.`;
  }
  return `Your OneDrive settings could not be read, so “${name}” was not attached. Try again in a moment.`;
}

/**
 * An automation's prompt and the files it carries, as one control.
 *
 * They are one control because they are one job. Every way of attaching a file ends in the same list —
 * the picker, an upload from this device, a document copied out of SharePoint, and typing `@` in the
 * prompt itself — and they have to share a cap, a dedup and a set of chips. Splitting the text field
 * from the files would mean lifting all of that into two hosts that only want to render a form.
 *
 * One component for both places an automation is authored or edited — the create window and the detail
 * page — because "the same picker, the same cap, the same `@`" is a promise two copies of this markup
 * could not keep. It owns nothing but its own transient state: the list and the
 * prompt belong to the caller, which is what knows how to save them.
 *
 * One file moves at a time. While an upload or a SharePoint copy is in flight every way of adding
 * another is shut, because each one merges into the list as it was when it started — see `busy`.
 *
 * There is no workspace picker anywhere in an automation, so without a workspace there is nowhere to
 * look for files: the buttons are not shown and the `@` trigger is dead, the way the composer's is
 * without a selected workspace. Chips still show: a file attached earlier is state the user must be able
 * to see and take off.
 */
export function AutomationPromptField({
  id,
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
  attachments,
  onAttachmentsChange,
  workspaceId,
  artifacts,
  skills,
  error = null,
}: {
  id: string;
  label: string;
  /** The prompt text, owned by the caller. */
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  attachments: AutomationAttachment[];
  onAttachmentsChange: (attachments: AutomationAttachment[]) => void;
  /** Where files are picked from — the automation's own workspace, or null when there is none. */
  workspaceId: string | null;
  /** The workspace's files, when they are known; only used to show a renamed file's current name. */
  artifacts?: readonly { id: string; name: string }[];
  /**
   * The `/` skill picker over the same prompt, when the host edits skills too. Left out where skills
   * do not apply — the detail page of an automation no agent runs — and then nothing about skills is
   * rendered.
   */
  skills?: UseAutomationSkillsFieldResult;
  /** Why the last save refused this prompt, shown under it; null while there is nothing to fix. */
  error?: string | null;
}): JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  /** Where the caret is, which is what decides whether an `@` token is being typed right now. */
  const [caretPos, setCaretPos] = useState(0);
  /**
   * The file being put into the workspace right now, by name — an upload from this device or a document
   * being copied out of SharePoint. One at a time: nothing here can abort.
   */
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadArtifact(workspaceId ?? '');
  /**
   * Whether a file is on its way into the workspace right now — an upload or a SharePoint copy.
   *
   * While one is, every way of adding a file is shut. Not for tidiness: an operation in flight is
   * holding the list as it was when it started, and merges into THAT when it lands. Anything attached
   * meanwhile — through `@`, through the picker, through a second upload — would be written over and
   * lost, with a chip on the screen the whole time to say it had arrived. Typing carries on; only the
   * ways in are closed.
   */
  const busy = upload.isPending || uploadingName !== null;
  /**
   * The list as it stands RIGHT NOW, for the two paths that finish after a wait.
   *
   * An upload's `onSuccess` and the import loop both used to merge into the list their render had
   * captured, so a chip taken off while a file was on its way came back when it landed. Reading the
   * ref instead means a landing merges into what the person is actually looking at. It is written on
   * both sides of the same fact: by the effect when the caller hands a new list down, and by
   * {@link updateAttachments} the moment one goes up — the caller's state lands a render later, and the
   * loop reads this again before then.
   */
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);
  /**
   * Whether this caller may put a NEW file into the workspace — an owner or an editor. A mirror of the
   * artifact owner's gate, not a replacement: it decides what to OFFER, and a 403 from the owner is
   * reported like any other upload failure.
   */
  const role = useCurrentWorkspaceRole(workspaceId ?? '', workspaceId !== null);
  const canUpload = workspaceId !== null && canAddArtifact(role);
  /**
   * The author's own OneDrive connection and the folders they opened in it, read only while the picker
   * is open — that is the one moment a file can be chosen out of them, and the tab in the picker reads
   * the very same two lists.
   *
   * They are read because attaching is decided by state the person owns: a connection that needs
   * reconnecting, or a folder left closed to automations, makes a live reference the agent will
   * never be able to open.
   */
  const oneDriveScopes = useQuery({
    ...oneDriveScopesQueryOptions(workspaceId ?? ''),
    enabled: pickerOpen && workspaceId !== null,
  });
  /**
   * One account per connection the folders come from. The refusal below turns on the account's
   * status, which belongs to the CONNECTION and cannot be read off a folder — and a person can reach
   * several Microsoft accounts now, so there is no single account to ask about.
   */
  const oneDriveConnectionIds = [
    ...new Set((oneDriveScopes.data ?? []).map((scope) => scope.connectionId)),
  ];
  const oneDriveAccounts = useQueries({
    queries: oneDriveConnectionIds.map((connectionId) => ({
      ...oneDriveAccountQueryOptions(workspaceId ?? '', connectionId),
      enabled: pickerOpen && workspaceId !== null,
    })),
  });
  const accountByConnection = new Map(
    oneDriveConnectionIds.map((connectionId, index) => [
      connectionId,
      oneDriveAccounts[index]?.data ?? null,
    ]),
  );
  /** Nothing to add from and nothing added: an explanation with no control under it is just noise. */
  const showFiles = workspaceId !== null || attachments.length > 0;

  /**
   * The `@` popup over the prompt, from the composer's own hook — same trigger, same Escape latch, same
   * highlight held by key. A row picked here goes through the very same paths the buttons below use, so
   * the cap and the dedup cannot differ by the way a file was chosen.
   */
  const mention = useMentionPopup({
    text: value,
    caretPos,
    workspaceId,
    suppressed: busy,
    onSelectArtifact: (artifact: WorkspaceArtifact) => {
      if (workspaceId === null) return;
      attachFiles([artifactAttachmentRef(workspaceId, artifact)]);
    },
    onSelectSharePointFile: (hit: SharePointSearchHit) => {
      void importSharePointFiles([
        sharePointAttachmentRef({
          /** The workspace's shared SharePoint: the popup searches that one and offers no choice. */
          connectionId: null,
          siteId: hit.siteId,
          driveId: hit.driveId,
          item: hit,
        }),
      ]);
    },
    onReplaceText: (nextValue: string, caret: number) => {
      onChange(nextValue);
      setCaretPos(caret);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    },
  });

  /**
   * Take a batch from the shared picker. It hands over the chat descriptor, which pins a revision; what
   * an automation stores is the same file without one, so every run reads the file as it is then.
   *
   * A pick the cap leaves out is refused out loud — losing the eleventh file in silence is how someone
   * ends up believing the automation reads a file it was never given. What was left out is read off the
   * merged list rather than counted, so a file that was already attached does not look like one the cap
   * dropped: it is in the list, it is simply not there twice.
   *
   * The remaining room is named only when there IS a remainder — with an empty list "only 10 more files
   * can be added (max 10)" states the cap twice and reads as a bug. The composer says it that way for
   * every count, and stays as it is; nothing about a message's attachments changes here.
   */
  /** The one refusal both ways of attaching share, in one place and in one wording. */
  function warnCapReached(): void {
    notify.warning(
      `Maximum ${MAX_AUTOMATION_ATTACHMENTS} files per automation`,
      { id: 'automation-attach-limit' },
    );
  }

  /** Hand a new list to the caller and keep the ref level with it, in that order. */
  function updateAttachments(next: AutomationAttachment[]): void {
    attachmentsRef.current = next;
    onAttachmentsChange(next);
  }

  function attachFiles(refs: ArtifactAttachmentRef[]): void {
    attachDescriptors(refs.map(automationAttachment));
  }

  /**
   * Attach files that stay in the author's own OneDrive. No bytes move and no artifact is created: the
   * automation carries the address, and the agent opens the file itself on every firing.
   *
   * Which is why a folder closed to background work is refused here rather than attached and left to
   * fail silently at three in the morning. The refusal states the setting that is in the way — it is
   * the author's own, on their own connection — and asks nothing: an automation never stops to request
   * consent, neither now nor when it runs.
   */
  function attachOneDriveFiles(refs: OneDriveAttachmentRef[]): void {
    const allowed = refs.filter((ref) => {
      const scope =
        oneDriveScopes.data?.find(
          (candidate) => candidate.scopeId === ref.scope_id,
        ) ?? null;
      const account = scope
        ? (accountByConnection.get(scope.connectionId) ?? null)
        : null;
      const blocked = oneDriveAutomationBlock(account, scope);
      if (blocked !== null) notify.error(oneDriveRefusal(blocked, ref.name));
      return blocked === null;
    });
    if (allowed.length > 0) {
      attachDescriptors(allowed.map(oneDriveAutomationAttachment));
    }
  }

  function attachDescriptors(picked: AutomationAttachment[]): void {
    const current = attachmentsRef.current;
    const merged = withAutomationAttachments(current, picked);
    const attached = new Set(merged.map(automationAttachmentKey));
    const leftOut = picked.filter(
      (file) => !attached.has(automationAttachmentKey(file)),
    );
    if (leftOut.length > 0) {
      const room = MAX_AUTOMATION_ATTACHMENTS - current.length;
      if (current.length === 0 || room <= 0) {
        warnCapReached();
      } else {
        notify.warning(
          `Only ${room} more file${room === 1 ? '' : 's'} can be added (max ${MAX_AUTOMATION_ATTACHMENTS})`,
          { id: 'automation-attach-limit' },
        );
      }
    }
    updateAttachments(merged);
  }

  /**
   * Put a file from this device into the workspace's files and attach what came back.
   *
   * The chat's own upload is deliberately not reused: its signed key is built under a conversation, and
   * an automation has files, not chat history. What lands here is a normal workspace artifact — private
   * to its author until they share it, which is the registry's own default and not a limitation of this
   * screen.
   *
   * The cap is checked BEFORE any bytes move: uploading a file that cannot be attached would leave the
   * user with a stray private artifact and no chip to show for it.
   */
  function startUpload(picked: File): void {
    if (attachmentsRef.current.length >= MAX_AUTOMATION_ATTACHMENTS) {
      warnCapReached();
      return;
    }
    const rejection = artifactUploadRejection(picked);
    if (rejection !== null) {
      notify.error(rejection);
      return;
    }
    setUploadingName(picked.name);
    upload.mutate(picked, {
      onSuccess: (created: WorkspaceArtifact) => {
        setUploadingName(null);
        if (workspaceId !== null) {
          attachFiles([artifactAttachmentRef(workspaceId, created)]);
        }
      },
      onError: (error: Error) => {
        setUploadingName(null);
        notify.error(error.message);
      },
    });
  }

  /**
   * Take documents out of SharePoint and put them in the workspace's files, one at a time, attaching each
   * artifact that comes back.
   *
   * The copy is what makes this work at all: a run fires with nobody at the screen and no SharePoint
   * session to borrow, so an address that only resolves live would be a file the automation can never
   * read. A refusal is shown and attaches nothing — a chip for a document that never arrived is exactly
   * the lie this feature exists to prevent.
   *
   * The cap is checked before each import so nothing is copied that could not be attached, and every
   * pass reads the list afresh: the props of the render this loop started in are a snapshot, and a file
   * taken off meanwhile would come back on the next import.
   */
  async function importSharePointFiles(
    refs: SharePointAttachmentRef[],
  ): Promise<void> {
    if (workspaceId === null) return;
    for (const ref of refs) {
      if (attachmentsRef.current.length >= MAX_AUTOMATION_ATTACHMENTS) {
        warnCapReached();
        break;
      }
      setUploadingName(ref.name);
      try {
        const imported = await putFileInWorkspace(workspaceId, ref);
        updateAttachments(
          withAutomationAttachments(attachmentsRef.current, [
            {
              artifact_id: imported.artifactId,
              name: imported.name,
              content_type: imported.contentType,
              size: imported.size,
            },
          ]),
        );
      } catch (error) {
        notify.error(
          error instanceof Error
            ? error.message
            : `Could not add “${ref.name}” to this workspace`,
        );
      } finally {
        setUploadingName(null);
      }
    }
  }

  /**
   * Take a chip off, and record it where a file still in the air will read it. Nothing is barred while
   * one is: removing is a control people expect to work, and the point is that the removal SURVIVES the
   * landing rather than that it is forbidden until then.
   */
  function removeAttachment(key: string): void {
    updateAttachments(
      attachmentsRef.current.filter(
        (file) => automationAttachmentKey(file) !== key,
      ),
    );
  }

  /**
   * The prompt itself. Two popups watch this one field — `@` for files and `/` for skills — and each
   * keeps its own caret, so every handler is offered to both. The value goes to both as well: the skills
   * hook has to see the edit as its own, or the next render reads it as the prompt being reseeded from
   * outside and puts the caret back to the start. Both write the same text, so telling both costs
   * nothing.
   */
  const promptTextarea = (
    <Textarea
      id={id}
      ref={(element) => {
        textareaRef.current = element;
        if (skills) skills.textareaRef.current = element;
      }}
      value={value}
      rows={rows}
      placeholder={placeholder}
      aria-invalid={error !== null}
      aria-describedby={error !== null ? fieldErrorId(id) : undefined}
      onChange={(event) => {
        onChange(event.target.value);
        setCaretPos(event.target.selectionStart ?? event.target.value.length);
        skills?.onChange(event);
      }}
      /** The token is read from the caret, so every way of moving it has to be heard. */
      onSelect={(event) => {
        setCaretPos(event.currentTarget.selectionStart ?? 0);
        skills?.onSelect(event);
      }}
      /**
       * The skill popup answers first: it is the one that can be open over a `/` token, and it says so by
       * preventing the default. Only an untouched key reaches the mention popup, so neither ever acts on
       * a key meant for the other.
       */
      onKeyDown={(event) => {
        skills?.onKeyDown(event);
        if (event.defaultPrevented) return;
        if (mention.onKeyDown(event.key, event.shiftKey)) {
          event.preventDefault();
        }
      }}
    />
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {skills?.skillsEnabled ? (
        <SlashCommandMenu
          open={skills.slashState !== null && skills.slashState.open}
          sections={skills.slashSections}
          query={skills.slashState?.query ?? ''}
          highlightIndex={skills.slashHighlightIndex}
          onHighlightChange={skills.setSlashHighlightIndex}
          onSelect={skills.selectSlashSkill}
          onDismiss={skills.dismissSlash}
        >
          {promptTextarea}
        </SlashCommandMenu>
      ) : (
        promptTextarea
      )}
      <FieldError fieldId={id} message={error} />
      <MentionMenu
        open={mention.state !== null && mention.state.open}
        sections={mention.sections}
        searching={mention.searching}
        highlightKey={mention.highlightKey}
        onHighlightChange={mention.setHighlightKey}
        onSelect={mention.selectEntry}
        onDismiss={mention.dismiss}
        anchorRef={textareaRef}
      />
      {skills?.skillsEnabled && (
        <>
          <SkillChips
            skills={skills.selectedSkills}
            onRemove={skills.removeSkill}
          />
          {skills.helperText && (
            <p className="text-caption text-muted-foreground">
              {skills.helperText}
            </p>
          )}
        </>
      )}

      {showFiles && (
        <div className="flex flex-col items-start gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {workspaceId !== null && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setPickerOpen(true)}
              >
                Add files
              </Button>
            )}
            {canUpload && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {upload.isPending ? 'Uploading…' : 'Upload from device'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  aria-label="Upload a file to attach"
                  onChange={(event) => {
                    const picked = event.target.files?.[0];
                    /** Cleared so picking the same file again fires `change` again. */
                    event.target.value = '';
                    if (picked) startUpload(picked);
                  }}
                />
              </>
            )}
          </div>
          {(attachments.length > 0 || uploadingName !== null) && (
            <ul className="flex flex-wrap gap-1.5">
              {attachments.map((file) => (
                <li
                  key={automationAttachmentKey(file)}
                  className="flex items-center gap-1.5"
                >
                  <AttachmentChip
                    variant="draft"
                    name={currentAttachmentName(file, artifacts)}
                    onRemove={() =>
                      removeAttachment(automationAttachmentKey(file))
                    }
                  />
                  {isOneDriveAutomationAttachment(file) && (
                    <span className="text-caption text-muted-foreground">
                      Your OneDrive
                    </span>
                  )}
                </li>
              ))}
              {uploadingName !== null && (
                <li>
                  <AttachmentChip
                    variant="draft"
                    name={uploadingName}
                    uploadState="uploading"
                  />
                </li>
              )}
            </ul>
          )}
          <p className="text-caption text-muted-foreground">
            Files are read in their latest version every time this runs.
          </p>
        </div>
      )}

      {pickerOpen && workspaceId !== null && (
        <AddArtifactsDialog
          workspaceId={workspaceId}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onAttachArtifacts={attachFiles}
          onAttachSharePointFiles={
            canUpload
              ? (refs) => {
                  void importSharePointFiles(refs);
                }
              : undefined
          }
          /**
           * Offered whatever this member may do in the workspace: the file stays in their own cloud,
           * so nothing is written here and there is no workspace permission to mirror.
           */
          onAttachOneDriveFiles={attachOneDriveFiles}
        />
      )}
    </div>
  );
}
